import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Refresh, FileDownload, ArrowBack, ErrorOutline } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { useSessionValidation } from '../../../utils/sessionValidation';
import LogoLoader from '../../common/LogoLoader';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const PRIMARY = '#1565c0';
const PRIMARY_DARK = '#0d47a1';
const HEADER_PARTICULARS = '#1a237e';
const HEADER_DEBIT = '#b71c1c';
const HEADER_CREDIT = '#1b5e20';

function TrialBalanceReport() {
    useSessionValidation();

    const history = useHistory();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();

    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [validationOpen, setValidationOpen] = useState(false);
    const [validationMsg, setValidationMsg] = useState('');

    const getFinancialYearStart = (date) => {
        const d = new Date(date + 'T00:00:00');
        const month = d.getMonth();
        const year = d.getFullYear();
        const fyStartYear = month < 3 ? year - 1 : year;
        return `${fyStartYear}-04-01`;
    };

    const getCurrentDate = () => new Date().toISOString().split('T')[0];

    const [toDate, setToDate] = useState(getCurrentDate());
    const [fromDate, setFromDate] = useState(getFinancialYearStart(toDate));

    useEffect(() => {
        if (toDate) {
            setFromDate(getFinancialYearStart(toDate));
        }
    }, [toDate]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (companyId && branchId && token) {
            fetchTrialBalance();
        }
    }, [companyId, branchId, token]);

    const fetchTrialBalance = async () => {
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            setValidationMsg('From Date cannot be greater than To Date.');
            setValidationOpen(true);
            return;
        }
        setDataItem([]);
        const loadingTimeout = setTimeout(() => {
            setLoading(true);
            setLoadingMessage('Loading Trial Balance...');
        }, 300);

        try {
            const response = await fetchData(
                'MISReportApi',
                `/TrailBalanceReport/${fromDate}/${toDate}/${companyId}/${branchId}`,
                token
            );
            if (response.responseCode === '1111') {
                setDataItem(response.response_data || []);
            } else {
                setDataItem(response.response_data || response || []);
            }
        } catch (err) {
            console.error('Error fetching Trial Balance:', err);
        } finally {
            clearTimeout(loadingTimeout);
            setLoading(false);
        }
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const totalDebit = filteredDataItem.reduce((sum, item) => sum + (parseFloat(item.TotalDebitSum) || 0), 0);
    const totalCredit = filteredDataItem.reduce((sum, item) => sum + (parseFloat(item.TotalCreditSum) || 0), 0);

    const formatCurrency = (value) => {
        const num = parseFloat(value) || 0;
        return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    // Group data by headName
    const getGroupedData = () => {
        const groups = [];
        let currentGroup = null;
        filteredDataItem.forEach((item) => {
            if (!currentGroup || currentGroup.headName !== item.headName) {
                currentGroup = { headName: item.headName, items: [] };
                groups.push(currentGroup);
            }
            currentGroup.items.push(item);
        });
        return groups;
    };

    const handleExportExcel = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'TrialBalanceReport', 0);
        if (!filteredDataItem.length) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Trial Balance');
        worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 4 }];

        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
        const fileName = `Trial_Balance_${formatDate(fromDate).replace(/\//g, '-')}_To_${formatDate(toDate).replace(/\//g, '-')}.xlsx`;

        const thinBorder = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        };

        // Row 1 - Company Name
        worksheet.mergeCells('A1:D1');
        const companyCell = worksheet.getCell('A1');
        companyCell.value = companyName || 'Company Name';
        companyCell.font = { bold: true, size: 14, color: { argb: 'FF1a237e' } };
        companyCell.alignment = { vertical: 'middle', horizontal: 'center' };
        companyCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe8eaf6' } };
        worksheet.getRow(1).height = 24;

        // Row 2 - Report Title & Date Range
        worksheet.mergeCells('A2:D2');
        const titleCell = worksheet.getCell('A2');
        titleCell.value = `Trial Balance - From: ${formatDate(fromDate)} To: ${formatDate(toDate)}`;
        titleCell.font = { bold: true, size: 11, color: { argb: 'FF37474f' } };
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // Row 3 - Generated info
        worksheet.mergeCells('A3:D3');
        const infoCell = worksheet.getCell('A3');
        infoCell.value = `Generated On: ${formattedDateTime} | By: ${username}`;
        infoCell.font = { italic: true, size: 9, color: { argb: 'FF757575' } };
        infoCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // Row 4 - Headers with unique colors matching UI
        const headers = ['Particulars (Head)', 'Particulars (Account)', 'Debit (₹)', 'Credit (₹)'];
        const headerRow = worksheet.addRow(headers);
        headerRow.height = 20;
        headerRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { vertical: 'middle', horizontal: colNumber >= 3 ? 'right' : 'center' };
            cell.border = thinBorder;
            // Unique colors per column
            if (colNumber <= 2) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1a237e' } }; // Indigo
            } else if (colNumber === 3) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFb71c1c' } }; // Red for Debit
            } else {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1b5e20' } }; // Green for Credit
            }
        });

        // Data rows - grouped with head name styling
        const groupedData = getGroupedData();
        groupedData.forEach((group) => {
            // Group header row
            const groupRow = worksheet.addRow([group.headName, '', '', '']);
            groupRow.eachCell((cell) => {
                cell.font = { bold: true, size: 10, color: { argb: 'FF1a237e' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe8eaf6' } };
                cell.alignment = { vertical: 'middle', horizontal: 'left' };
                cell.border = thinBorder;
            });
            worksheet.mergeCells(groupRow.number, 1, groupRow.number, 2);

            // Account rows within group
            group.items.forEach((item, idx) => {
                const row = worksheet.addRow([
                    '',
                    item.AccountName || '',
                    parseFloat(item.TotalDebitSum) || 0,
                    parseFloat(item.TotalCreditSum) || 0
                ]);
                row.eachCell((cell, colNumber) => {
                    cell.border = thinBorder;
                    cell.alignment = { vertical: 'middle' };
                    if (colNumber === 2) {
                        cell.font = { size: 10 };
                        cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 2 };
                    }
                    if (colNumber === 3) {
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                        cell.numFmt = '₹#,##0.00';
                        cell.font = { size: 10, color: { argb: parseFloat(item.TotalDebitSum) > 0 ? 'FFc62828' : 'FFbdbdbd' } };
                    }
                    if (colNumber === 4) {
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                        cell.numFmt = '₹#,##0.00';
                        cell.font = { size: 10, color: { argb: parseFloat(item.TotalCreditSum) > 0 ? 'FF2e7d32' : 'FFbdbdbd' } };
                    }
                    // Alternating row fill
                    if (idx % 2 === 1) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFfafbfe' } };
                    }
                });
            });
        });

        // Grand Total row
        const totRow = worksheet.addRow(['', 'Grand Total', totalDebit, totalCredit]);
        totRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0d47a1' } };
            cell.border = thinBorder;
            if (colNumber === 3) {
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
                cell.numFmt = '₹#,##0.00';
                cell.font = { bold: true, size: 11, color: { argb: 'FFffcdd2' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0d47a1' } };
            } else if (colNumber === 4) {
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
                cell.numFmt = '₹#,##0.00';
                cell.font = { bold: true, size: 11, color: { argb: 'FFa5d6a7' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0d47a1' } };
            } else {
                cell.alignment = { horizontal: 'left', vertical: 'middle' };
            }
        });
        worksheet.mergeCells(totRow.number, 1, totRow.number, 2);

        worksheet.columns = [
            { width: 25 }, { width: 30 }, { width: 18 }, { width: 18 }
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    const styles = {
        container: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: '#f4f6f9',
            fontFamily: "'Segoe UI', 'Roboto', sans-serif",
            boxSizing: 'border-box',
            overflow: 'hidden'
        },
        card: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: '#ffffff',
            borderRadius: 0,
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
        },
        toolbar: {
            display: 'flex',
            alignItems: 'center',
            padding: isMobile ? '6px 10px' : '7px 16px',
            background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY} 100%)`,
            gap: 8,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(13,71,161,0.3)'
        },
        dateInput: {
            height: 28,
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: 4,
            padding: '0 8px',
            fontSize: 11,
            backgroundColor: 'rgba(255,255,255,0.12)',
            color: '#fff',
            colorScheme: 'dark',
            flexShrink: 0,
            outline: 'none',
            transition: 'border-color 0.2s, background-color 0.2s'
        },
        searchInput: {
            height: 28,
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: 4,
            padding: '0 10px',
            fontSize: 11,
            backgroundColor: 'rgba(255,255,255,0.12)',
            color: '#fff',
            colorScheme: 'dark',
            flexShrink: 0,
            width: isMobile ? 100 : 140,
            outline: 'none',
            transition: 'border-color 0.2s, background-color 0.2s'
        },
        tableContainer: {
            flex: 1,
            overflow: 'auto',
            scrollbarWidth: 'thin',
            position: 'relative'
        },
        table: {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            fontSize: isMobile ? 11 : 12.5
        },
        thParticulars: {
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: HEADER_PARTICULARS,
            color: '#ffffff',
            fontWeight: 700,
            fontSize: isMobile ? 9 : 10,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            padding: isMobile ? '5px 8px' : '6px 14px',
            borderBottom: '2px solid #283593',
            whiteSpace: 'nowrap',
            textAlign: 'left'
        },
        thDebit: {
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: HEADER_DEBIT,
            color: '#ffffff',
            fontWeight: 700,
            fontSize: isMobile ? 9 : 10,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            padding: isMobile ? '5px 8px' : '6px 14px',
            borderBottom: '2px solid #c62828',
            whiteSpace: 'nowrap',
            textAlign: 'right'
        },
        thCredit: {
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: HEADER_CREDIT,
            color: '#ffffff',
            fontWeight: 700,
            fontSize: isMobile ? 9 : 10,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            padding: isMobile ? '5px 8px' : '6px 14px',
            borderBottom: '2px solid #2e7d32',
            whiteSpace: 'nowrap',
            textAlign: 'right'
        },
        groupHeader: {
            background: 'linear-gradient(90deg, #e8eaf6 0%, #f3f4fd 100%)',
            borderBottom: '1px solid #c5cae9',
            borderTop: '1px solid #e8eaf6'
        },
        groupHeaderText: {
            padding: isMobile ? '3px 8px' : '4px 14px',
            fontSize: isMobile ? 10.5 : 11.5,
            fontWeight: 700,
            color: '#1a237e',
            letterSpacing: '0.2px',
            textTransform: 'capitalize'
        },
        dataRow: {
            borderBottom: '1px solid #f0f0f0',
            transition: 'background-color 0.15s ease'
        },
        accountCell: {
            padding: isMobile ? '2px 8px 2px 20px' : '3px 14px 3px 32px',
            fontSize: isMobile ? 11 : 12,
            color: '#37474f',
            fontWeight: 400,
            borderRight: '1px solid #f5f5f5'
        },
        amountCell: {
            padding: isMobile ? '2px 8px' : '3px 14px',
            fontSize: isMobile ? 11 : 12,
            fontWeight: 600,
            textAlign: 'right',
            fontFamily: "'Consolas', 'Courier New', monospace",
            whiteSpace: 'nowrap',
            borderRight: '1px solid #f5f5f5'
        },
        totalRow: {
            background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, #1565c0 100%)`,
            flexShrink: 0
        },
        totalLabel: {
            padding: isMobile ? '5px 8px' : '6px 14px',
            fontSize: isMobile ? 11 : 12,
            fontWeight: 700,
            color: '#ffffff',
            letterSpacing: '0.3px'
        },
        totalAmount: {
            padding: isMobile ? '5px 8px' : '6px 14px',
            fontSize: isMobile ? 12 : 13,
            fontWeight: 700,
            textAlign: 'right',
            fontFamily: "'Consolas', 'Courier New', monospace",
            whiteSpace: 'nowrap'
        },
        emptyState: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            color: '#90a4ae'
        }
    };

    return (
        <div style={styles.container}>
            <LogoLoader open={loading} message={loadingMessage} />

            {/* Validation Modal */}
            <Dialog
                open={validationOpen}
                onClose={() => setValidationOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minWidth: 340,
                        p: 0,
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    background: 'linear-gradient(135deg, #ff5252 0%, #d32f2f 100%)',
                    color: '#fff',
                    py: 1.5,
                    px: 2.5,
                    fontSize: 16,
                    fontWeight: 700
                }}>
                    <ErrorOutline sx={{ fontSize: 22 }} />
                    Validation Error
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
                    <Typography sx={{ fontSize: 14, color: '#37474f', fontWeight: 500 }}>
                        {validationMsg}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2 }}>
                    <Button
                        onClick={() => setValidationOpen(false)}
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            px: 3,
                            fontWeight: 600,
                            background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' }
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <div style={styles.card}>
                {/* Toolbar */}
                <div style={styles.toolbar}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={{ color: '#fff', flexShrink: 0, p: 0.4, '&:hover': { background: 'rgba(255,255,255,0.15)' } }}>
                            <ArrowBack sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0, letterSpacing: '0.3px' }}>Trial Balance</span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={styles.dateInput} />
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={styles.dateInput} />
                    <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={styles.searchInput} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchTrialBalance} size="small" sx={{ color: '#fff', flexShrink: 0, p: 0.4, '&:hover': { background: 'rgba(255,255,255,0.15)' } }}>
                            <Refresh sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExportExcel} size="small" sx={{ color: '#fff', flexShrink: 0, p: 0.4, '&:hover': { background: 'rgba(255,255,255,0.15)' } }}>
                            <FileDownload sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Scrollable Table */}
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.thParticulars, width: '50%' }}>Particulars</th>
                                <th style={{ ...styles.thDebit, width: '25%' }}>Debit (₹)</th>
                                <th style={{ ...styles.thCredit, width: '25%' }}>Credit (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDataItem.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan={3} style={styles.emptyState}>
                                        <Typography sx={{ fontSize: 14, color: '#90a4ae', fontWeight: 500 }}>
                                            No data available
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: '#b0bec5', mt: 0.5 }}>
                                            Adjust dates or refresh to load data
                                        </Typography>
                                    </td>
                                </tr>
                            ) : (
                                getGroupedData().map((group, gIdx) => (
                                    <React.Fragment key={gIdx}>
                                        {/* Group Header */}
                                        <tr style={styles.groupHeader}>
                                            <td colSpan={3} style={styles.groupHeaderText}>
                                                {group.headName}
                                            </td>
                                        </tr>
                                        {/* Account Rows */}
                                        {group.items.map((item, idx) => (
                                            <tr
                                                key={item.headid || `${gIdx}-${idx}`}
                                                style={{
                                                    ...styles.dataRow,
                                                    backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafbfe'
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e3f2fd'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#ffffff' : '#fafbfe'; }}
                                            >
                                                <td style={styles.accountCell}>{item.AccountName}</td>
                                                <td style={{ ...styles.amountCell, color: parseFloat(item.TotalDebitSum) > 0 ? '#c62828' : '#9e9e9e' }}>
                                                    {parseFloat(item.TotalDebitSum) > 0 ? formatCurrency(item.TotalDebitSum) : '-'}
                                                </td>
                                                <td style={{ ...styles.amountCell, color: parseFloat(item.TotalCreditSum) > 0 ? '#2e7d32' : '#9e9e9e' }}>
                                                    {parseFloat(item.TotalCreditSum) > 0 ? formatCurrency(item.TotalCreditSum) : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Grand Total - Fixed at bottom */}
                {filteredDataItem.length > 0 && (
                    <table style={{ ...styles.table, borderCollapse: 'collapse', flexShrink: 0 }}>
                        <tfoot>
                            <tr style={styles.totalRow}>
                                <td style={{ ...styles.totalLabel, width: '50%' }}>Grand Total</td>
                                <td style={{ ...styles.totalAmount, width: '25%', color: '#ffcdd2' }}>{formatCurrency(totalDebit)}</td>
                                <td style={{ ...styles.totalAmount, width: '25%', color: '#a5d6a7' }}>{formatCurrency(totalCredit)}</td>
                            </tr>
                        </tfoot>
                    </table>
                )}
            </div>
        </div>
    );
}

export default TrialBalanceReport;
